import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors as C } from "@/lib/theme";
import { Ion } from "@/components/Ion";
import { Screen, Spinner } from "@/components/Orig";
import { uploadToDrive } from "@/lib/driveUpload";
import { useAuth } from "@/context/AuthContext";

const MAX = 5 * 1024 ** 3;
const mb = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${Math.max(1, Math.round(b / 1024 ** 2))} MB`);

const Header = ({ navigate }) => (
  <div className="flex items-center gap-3 py-3">
    <button onClick={() => navigate(-1)} aria-label="Back" data-testid="mv-back" className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border" style={{ background: C.card, borderColor: C.border }}><Ion name="chevron-back" size={22} color={C.text} /></button>
    <div className="flex-1"><p className="text-[9px] font-extrabold tracking-[1.6px]" style={{ color: C.accent }}>MARKETING</p><h1 className="text-lg font-extrabold" data-testid="page-title">Upload Marketing Videos</h1></div>
  </div>
);

const UploadForm = ({ onDone }) => {
  const [file, setFile] = useState(null);
  const [desc, setDesc] = useState("");
  const [consent, setConsent] = useState(false);
  const [pct, setPct] = useState(null);
  const input = useRef(null);
  const pick = (f) => { if (f && f.size > MAX) { toast.error("That video is over 5 GB. Please trim it or export it smaller."); return; } setFile(f || null); };
  const go = async () => {
    setPct(0);
    try {
      const s = await api("/marketing-videos/uploads", { method: "POST", body: { filename: file.name, size: file.size, mime_type: file.type || null, description: desc, consent, origin: window.location.origin } });
      const g = await uploadToDrive(s.upload_url, file, setPct);
      await api(`/marketing-videos/uploads/${s.id}/complete`, { method: "POST", body: { drive_file_id: g.id } });
      toast.success("Video uploaded. Thank you!");
      setFile(null); setDesc(""); setConsent(false); if (input.current) input.current.value = "";
      onDone();
    } catch (e) { toast.error(e.message); }
    setPct(null);
  };
  const busy = pct !== null;
  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.border }} data-testid="mv-form">
      <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center" style={{ borderColor: file ? C.accent : C.border }} data-testid="mv-pick">
        <Ion name="videocam-outline" size={30} color={C.accent} />
        <span className="text-sm font-bold" style={{ color: C.text }}>{file ? file.name : "Tap to choose a video"}</span>
        <span className="text-xs" style={{ color: C.textSecondary }}>{file ? mb(file.size) : "From your phone's camera roll or your computer · up to 5 GB"}</span>
        <input ref={input} type="file" accept="video/*" className="hidden" disabled={busy} onChange={(e) => pick(e.target.files?.[0])} data-testid="mv-file-input" />
      </label>
      <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} maxLength={500} disabled={busy} placeholder="Short description, e.g. Rust removal on a trailer frame, before and after"
        className="mt-3 w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none" style={{ borderColor: C.border, color: C.text }} data-testid="mv-description" />
      <label className="mt-3 flex items-start gap-2 text-xs" style={{ color: C.text }}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={busy} className="mt-0.5 h-4 w-4 accent-[#4CCC06]" data-testid="mv-consent" />
        <span>I give Advanced Laser Restoration permission to use this video, in whole or in part, in its marketing and advertising, and I confirm everyone shown agreed to be filmed.</span>
      </label>
      {busy && <div className="mt-3" data-testid="mv-progress"><div className="h-2 overflow-hidden rounded-full" style={{ background: C.border }}><div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${Math.round(pct * 100)}%`, background: C.accent }} /></div>
        <p className="mt-1 text-xs" style={{ color: C.textSecondary }}>Uploading… {Math.round(pct * 100)}%. Keep this screen open until it finishes.</p></div>}
      <button onClick={go} disabled={!file || desc.trim().length < 3 || !consent || busy} className="mt-3 w-full rounded-[10px] py-3 text-sm font-extrabold text-black disabled:opacity-40" style={{ background: C.accent }} data-testid="mv-submit">{busy ? "Uploading…" : "Upload video"}</button>
    </div>
  );
};

export default function MarketingVideos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const [s, mine] = await Promise.all([api("/marketing-videos/drive/status"), api("/marketing-videos/uploads/mine")]);
    setSt(s); setRows(mine);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(e.message)); }, [load]);
  return (
    <Screen testId="marketing-videos-page">
      <div className="mx-auto max-w-xl px-4 pb-16">
        <Header navigate={navigate} />
        <p className="mb-4 text-sm" style={{ color: C.textSecondary }}>Send your best laser cleaning videos to corporate. We use them for ALR ads, social media and the website.</p>
        {user?.role === "admin" && <button onClick={() => navigate("/admin/marketing-videos")} className="mb-4 w-full rounded-[10px] border px-3 py-2 text-sm font-bold" style={{ borderColor: C.accent, color: C.accent }} data-testid="mv-admin-link">Admin: connect Google Drive and see all uploads</button>}
        {!st ? <Spinner /> : !st.ready
          ? <p className="rounded-xl border p-4 text-sm" style={{ borderColor: C.border, color: C.text }} data-testid="mv-not-ready">Video uploads aren't switched on yet. Corporate is setting up the video Drive. Please check back soon.</p>
          : <UploadForm onDone={load} />}
        <p className="mb-2 mt-6 text-[10px] font-extrabold tracking-[1.4px]" style={{ color: C.accent }}>YOUR UPLOADS</p>
        {!rows.length ? <p className="text-sm" style={{ color: C.textSecondary }} data-testid="mv-empty">No videos uploaded yet.</p> : rows.map((r) => (
          <div key={r.id} className="mb-2 rounded-lg border px-3 py-2" style={{ borderColor: C.border }} data-testid={`mv-row-${r.id}`}>
            <p className="text-sm font-bold" style={{ color: C.text }}>{r.filename} <span className="font-normal" style={{ color: C.textSecondary }}>· {mb(r.size)} · {new Date(r.uploaded_at).toLocaleDateString()}</span></p>
            <p className="text-xs" style={{ color: C.textSecondary }}>{r.description}</p>
          </div>
        ))}
      </div>
    </Screen>
  );
}
